import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { Rate } from '../../../shared/types/Rate';
import { MoreHorizontalIcon, PlusIcon, GripVerticalIcon } from 'lucide-react';
export function Rates() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data based on the screenshot
  const [rates, setRates] = useState<Rate[]>([{
    id: '1',
    name: 'Test With Scuba Spa',
    description: 'Scuba spa test',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES',
    baseNightlyRate: 100.0,
    cancellationPlan: 'NON_REFUNDABLE',
    status: 'inactive',
    isPromo: true
  }, {
    id: '2',
    name: 'Advance Booking with Free Seaplan Transfer',
    description: 'Plan your Maldivan escape early and enjoy the added luxury of a complimentary round-trip seaplane transfer from Malé to the resort. This exclusive offer rewards advance planners with significant savings on transportation costs.',
    roomType: 'DELUXE',
    promoCode: 'EXPERIENCEMALDIVES2025',
    baseNightlyRate: 100.0,
    cancellationPlan: 'FLEXIBLE',
    status: 'active',
    isPromo: true
  }]);
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [roomTypeFilter, setRoomTypeFilter] = useState<string>('all');
  // Filter rates based on search term and filters
  const filteredRates = rates.filter(rate => {
    const matchesSearch = rate.name.toLowerCase().includes(searchTerm.toLowerCase()) || rate.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || statusFilter === 'active' && rate.status === 'active' || statusFilter === 'inactive' && rate.status === 'inactive';
    const matchesRoomType = roomTypeFilter === 'all' || rate.roomType === roomTypeFilter;
    return matchesSearch && matchesStatus && matchesRoomType;
  });
  // Format currency for display
  function formatCurrency(amount: number): string {
    return formatCurrencyAmount(amount, {
      fractionDigits: 2
    });
  }
  // Format cancellation plan for display
  function formatCancellationPlan(plan: string): string {
    return plan.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }
  return <div className="space-y-6" data-id="element-9252">
      <div className="flex justify-between items-center" data-id="element-9253">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-9254">
          {t('pages.Rates.rates')}
        </h1>
        <button className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md flex items-center" data-id="element-9255">
          <PlusIcon size={18} className="mr-1" data-id="element-9256" />
          {t('pages.Rates.add_rates')}
        </button>
      </div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3" data-id="element-9257">
        <input type="search" aria-label={t('pages.Rates.attrs.search_rates')} placeholder={t('pages.Rates.attrs.search_rates')} className="border border-gray-300 rounded-md px-3 py-2 w-48" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-9258" />

        <select className="border border-gray-300 rounded-md px-3 py-2" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-9259">
          <option value="all" data-id="element-9260">{t('components.AddRoomForm.status')}</option>
          <option value="active" data-id="element-9261">{t('pages.Rates.active')}</option>
          <option value="inactive" data-id="element-9262">{t('pages.Rates.inactive')}</option>
        </select>
        <select className="border border-gray-300 rounded-md px-3 py-2" value={roomTypeFilter} onChange={e => setRoomTypeFilter(e.target.value)} data-id="element-9263">
          <option value="all" data-id="element-9264">{t('pages.Rates.room_type')}</option>
          <option value="DELUXE" data-id="element-9265">{t('pages.Rates.deluxe')}</option>
          <option value="SUPERIOR" data-id="element-9266">{t('pages.Rates.superior')}</option>
          <option value="OVERWATER_VILLA" data-id="element-9267">
            {t('pages.Rates.overwater_villa')}
          </option>
        </select>
      </div>
      {/* Rates List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-9268">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-9269">
          <thead className="bg-gray-50" data-id="element-9270">
            <tr data-id="element-9271">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9272">
                {t('pages.Rates.details')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9273">
                {t('pages.Rates.promo_code')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9274">
                {t('pages.Rates.base_nightly_rate')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9275">
                {t('pages.Rates.cancellation_plan')}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9276">
                {t('components.AddRoomForm.status')}
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9277">
                {t('components.RoomAssets.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-9278">
            {filteredRates.map(rate => <tr key={rate.id} data-id="element-9279">
                <td className="px-6 py-4" data-id="element-9280">
                  <div className="flex items-start" data-id="element-9281">
                    <GripVerticalIcon size={16} className="text-gray-400 mt-1 mr-2" data-id="element-9282" />

                    <div data-id="element-9283">
                      <div className="text-sm font-medium text-gray-900" data-id="element-9284">
                        {rate.name}
                      </div>
                      <div className="text-xs text-gray-500 mt-1 max-w-lg" data-id="element-9285">
                        {rate.description}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9286">
                  <div className="text-sm text-gray-900" data-id="element-9287">{rate.promoCode}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9288">
                  <div className="text-sm text-gray-900" data-id="element-9289">
                    {formatCurrency(rate.baseNightlyRate)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9290">
                  <div className="text-sm text-gray-900" data-id="element-9291">
                    {formatCancellationPlan(rate.cancellationPlan)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-9292">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${rate.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-9293">
                    {rate.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  {rate.isPromo && <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-9294">
                      {t('pages.Rates.promo')}
                    </span>}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-9295">
                  <button aria-label="Rate actions" className="text-gray-400 hover:text-gray-500" data-id="element-9296">
                    <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-9297" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}